import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, Users, Calendar, ChevronRight, X, Sparkles } from 'lucide-react';

interface OnboardingFlowProps {
  onDismiss: () => void;
}

const STEPS = [
  {
    icon: CalendarCheck,
    title: 'Beschikbaarheid opgeven',
    description:
      'Laat weten wanneer je wel en niet kunt. Zo zorgen we ervoor dat je alleen wordt ingeroosterd als het jou uitkomt.',
    action: 'Naar beschikbaarheid',
    route: '/beschikbaarheid',
    color: 'bg-amber-100 text-amber-600',
    activeColor: 'border-amber-500 bg-amber-50',
    buttonColor: 'bg-amber-600 hover:bg-amber-700',
  },
  {
    icon: Users,
    title: 'Word lid van een team',
    description:
      'Kies een of meerdere teams waar je bij wilt aansluiten. Denk aan muziek, techniek, oppas of ontvangst.',
    action: 'Naar teams',
    route: '/teams',
    color: 'bg-emerald-100 text-emerald-600',
    activeColor: 'border-emerald-500 bg-emerald-50',
    buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
  },
  {
    icon: Calendar,
    title: 'Bekijk je rooster',
    description:
      'Zodra je bent ingedeeld, vind je hier je komende diensten. Je kunt ook eenvoudig ruilen met anderen.',
    action: 'Naar mijn rooster',
    route: '/mijn-rooster',
    color: 'bg-amber-100 text-amber-600',
    activeColor: 'border-amber-500 bg-amber-50',
    buttonColor: 'bg-amber-600 hover:bg-amber-700',
  },
];

export default function OnboardingFlow({ onDismiss }: OnboardingFlowProps) {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  function handleStepAction() {
    const step = STEPS[activeStep];
    navigate(step.route);
    onDismiss();
  }

  function handleNext() {
    if (activeStep < STEPS.length - 1) {
      setActiveStep(activeStep + 1);
    } else {
      onDismiss();
    }
  }

  const current = STEPS[activeStep];
  const Icon = current.icon;

  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-lg overflow-hidden animate-fade-in">
      <div className="relative bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5 text-white">
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 mb-1">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <h2 className="text-lg font-bold">Welkom bij kerkplanner.app</h2>
        </div>
        <p className="text-slate-300 text-sm">
          In 3 stappen ben je helemaal klaar om aan de slag te gaan.
        </p>
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setActiveStep(idx)}
              className="flex items-center gap-2 flex-1"
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  idx === activeStep
                    ? 'bg-slate-800 text-white scale-110'
                    : idx < activeStep
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-400'
                }`}
              >
                {idx < activeStep ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  idx + 1
                )}
              </div>
              {idx < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 rounded ${
                    idx < activeStep ? 'bg-emerald-300' : 'bg-slate-200'
                  }`}
                />
              )}
            </button>
          ))}
        </div>

        <div className={`rounded-xl border-2 p-5 transition-all ${current.activeColor}`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${current.color}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-slate-800 text-lg">{current.title}</h3>
              <p className="text-slate-600 text-sm mt-1 leading-relaxed">{current.description}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={handleNext}
            className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            {activeStep < STEPS.length - 1 ? 'Volgende stap' : 'Sluiten'}
          </button>
          <button
            onClick={handleStepAction}
            className={`flex items-center gap-2 px-5 py-2.5 text-white text-sm font-medium rounded-lg transition-colors ${current.buttonColor}`}
          >
            {current.action}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
